import React from 'react';
import { centsToPrice } from '@/utils/formatter';
import i18n from '@/utils/i18n';
import { ContainerType } from '../OrderFulfillment/Components/OrderFulfillmentAddContainerTypes';
import {
  Table,
  TableHeader,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from '@/components/ui/table';
import { GetEventQuery } from '../../../graphql/queries/getEvent.generated';
import ContainerTypeDisplay from '../../../components/ContainerType/ContainerTypeDisplay';
import { ItemTypeDisplay } from '@/components/ItemType/ItemTypeDisplay';
import { EventPriceKind } from '../../../graphql/generatedTypes';
import {
  EventPriceTarget,
  PricedItemType,
  priceTarget,
} from './eventPriceTarget';
import { Button } from '@/components/ui/button';

export type EventPrice = NonNullable<GetEventQuery['event']>['prices'][0];

const eventPriceTypes = [
  'rental_price',
  'cleaning_price',
  'deposit_price',
  'lost_price',
  'logistic_partner_cleaning_price',
] as const;
export type EventPriceType = (typeof eventPriceTypes)[number];

type StringifyPriceProperties<T> = {
  [K in keyof T]: K extends `${string}_price` ? string : T[K];
};

export type StringifiedEventPrice = StringifyPriceProperties<EventPrice>;

export type DefaultContainerPriceMap = Record<
  number,
  Partial<Record<EventPriceType, string>>
>;

interface EventPriceTableProps {
  error: any;
  loading: boolean;
  prices: StringifiedEventPrice[];
  allContainerTypes: ContainerType[];
  allItemTypes?: PricedItemType[];
  currency: string;
  setPrice: (
    priceType: EventPriceType,
    target: EventPriceTarget,
    value: string,
  ) => void;
  savePrices: () => void;
  showDefaultColumn: boolean;
  defaultContainerPrices?: DefaultContainerPriceMap;
}

const EventPriceTable: React.FC<EventPriceTableProps> = ({
  error,
  loading,
  prices,
  allContainerTypes,
  allItemTypes,
  currency,
  setPrice,
  savePrices: saveEvent,
  showDefaultColumn,
  defaultContainerPrices,
}) => {
  const hasItemTypeRow = prices.some(
    (price) => priceTarget(price).kind === EventPriceKind.ItemType,
  );

  return (
    <div className="tw:space-y-6">
      <div className="tw:flex tw:justify-between tw:items-center tw:pb-4 tw:border-b tw:border-gray-200">
        <h3 className="tw:text-xl tw:font-semibold tw:text-gray-900">
          {i18n.t('Event:prices')}
        </h3>
        <Button onClick={saveEvent} disabled={loading}>
          {loading ? i18n.t('Common:loading') : i18n.t('Common:save')}
        </Button>
      </div>
      {hasItemTypeRow && (
        <div className="tw:p-3 tw:bg-amber-50 tw:border tw:border-amber-200 tw:rounded-md tw:text-amber-800">
          {i18n.t('Event:itemTypePricesHint')}
        </div>
      )}
      {loading ? (
        <div className="tw:py-4 tw:text-center">
          <div
            className="tw:inline-block tw:h-8 tw:w-8 tw:animate-spin tw:rounded-full tw:border-4 tw:border-solid tw:border-current tw:border-r-transparent tw:text-primary"
            role="status"
          >
            <span className="tw:sr-only">{i18n.t('Common:loading')}</span>
          </div>
        </div>
      ) : error ? (
        <div className="tw:p-3 tw:bg-red-50 tw:border tw:border-red-200 tw:rounded-md tw:text-red-700">
          {i18n.t('Common:error')}
        </div>
      ) : (
        <Table className="tw:bg-card tw:rounded-md">
          <TableHeader>
            <TableRow className="tw:[&>*:first-child]:pl-4 tw:[&>*:last-child]:pr-4">
              <TableHead>{i18n.t('Common:name')}</TableHead>
              {eventPriceTypes.map((priceType) => (
                <TableHead key={priceType}>
                  {i18n.t(`Event:${priceType}`)}
                </TableHead>
              ))}
              {showDefaultColumn && (
                <TableHead>{i18n.t('Event:default')}</TableHead>
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {prices.map((price) => {
              const target = priceTarget(price);
              const isContainer = target.kind === EventPriceKind.ContainerType;
              const itemType = isContainer
                ? undefined
                : allItemTypes?.find((it) => it.id === target.id);
              return (
                <TableRow
                  key={`${target.kind}:${target.id}`}
                  data-price-kind={target.kind}
                  data-price-target-id={target.id}
                  className="tw:[&>*:first-child]:pl-4 tw:[&>*:last-child]:pr-4"
                >
                  <TableCell>
                    {isContainer ? (
                      <ContainerTypeDisplay
                        containerType={allContainerTypes.find(
                          (c) => c.id === target.id,
                        )}
                        showContainerId={false}
                        size="s"
                      />
                    ) : (
                      <div className="tw:flex tw:items-center tw:gap-2">
                        <ItemTypeDisplay
                          alt={itemType?.name ?? ''}
                          hexCode={itemType?.color_hex_code}
                          imageUrl={itemType?.image_url}
                          className="tw:size-10"
                          showLargeImageOnHover
                        />
                        <span className="tw:text-sm tw:font-medium">
                          {itemType?.name}
                        </span>
                      </div>
                    )}
                  </TableCell>
                  {eventPriceTypes.map((priceType) => {
                    const rawValue = price[priceType];
                    const displayValue =
                      rawValue == null || rawValue === ''
                        ? ''
                        : String(rawValue);
                    // DefaultContainerInformation is keyed by container type id, and
                    // container type 3 and item type 3 both exist.
                    const defaultCents = isContainer
                      ? defaultContainerPrices?.[target.id]?.[priceType]
                      : undefined;
                    const placeholderPrice =
                      defaultCents != null
                        ? centsToPrice(parseInt(defaultCents), currency)
                        : '';
                    return (
                      <TableCell key={priceType}>
                        <div className="tw:flex">
                          <input
                            value={displayValue}
                            placeholder={placeholderPrice}
                            type="text"
                            className="tw:flex-1 tw:max-w-30 tw:px-3 tw:py-2 tw:border tw:border-r-0 tw:border-gray-300 tw:rounded-l-md tw:focus:outline-none tw:focus:ring-2 tw:focus:ring-primary"
                            onChange={(e) =>
                              setPrice(priceType, target, e.target.value)
                            }
                          />
                          <span className="tw:inline-flex tw:items-center tw:px-3 tw:py-2 tw:border tw:border-gray-300 tw:rounded-r-md tw:bg-gray-100 tw:text-gray-700">
                            {displayValue === ''
                              ? placeholderPrice
                              : centsToPrice(parseInt(displayValue), currency)}
                          </span>
                        </div>
                      </TableCell>
                    );
                  })}
                  {showDefaultColumn && (
                    <TableCell>
                      {price.is_default
                        ? i18n.t('Common:yes')
                        : i18n.t('Common:no')}
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </div>
  );
};

export default EventPriceTable;
